import { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { CatalogCategoryKindType } from '@econtractor/types';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useToast } from 'context/ToastContext';
import EToolTip from 'components/others/EToolTip';
import FormInput from 'components/forms/FormInput';
import SolarIcon from 'components/icons/SolarIcon';
import { CATALOG_DIR_I18N_NS, catalogDirApi } from '../model/catalogDirApi';

interface DirItem {
    ID: number;
    name: string;
}

interface DirsListProps {
    kind: CatalogCategoryKindType;
    items: DirItem[];
    usage: Record<number, number>;
    onChanged: () => Promise<void>;
}

const DirsList = ({ kind, items, usage, onChanged }: DirsListProps) => {
    const { t } = useTranslation([CATALOG_DIR_I18N_NS[kind], 'common']);
    const { showDlg } = useModal();
    const { showToast } = useToast();
    const [editId, setEditId] = useState<number | null>(null);

    const handleRename = async (item: DirItem, value: string | number) => {
        const name = String(value || '').trim();
        if (name === item.name) {
            setEditId(null);
            return;
        }

        const res = await catalogDirApi[kind].rename(item.ID, name);
        if (res?.stat) {
            await onChanged();
            setEditId(null);
            showToast(t('dirs.messRenamed'), 'success');
            return;
        }

        if (res?.errCode === 'DUPLICATE_NAME') {
            showToast(t('dirs.messDuplicate'), 'danger');
            return false;
        }
        if (res?.errCode === 'EMPTY_NAME') {
            showToast(t('dirs.messEmpty'), 'danger');
            return false;
        }
        if (res?.errCode === 'NAME_TOO_LONG') {
            showToast(t('dirs.messTooLong'), 'danger');
            return false;
        }

        showToast(t('common:messError'), 'danger');
        return false;
    };

    const handleDelete = (item: DirItem) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            const res = await catalogDirApi[kind].delete(item.ID);
            if (res?.stat) {
                await onChanged();
                showToast(t('dirs.messDeleted'), 'success');
                return;
            }
            if (res?.errCode === 'IN_USE') {
                showToast(t('dirs.messInUse'), 'danger');
                return;
            }
            showToast(t('common:messError'), 'danger');
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: t('dirs.confirmDelete', { name: item.name }),
        });
    };

    if (!items.length) {
        return <p className="text-muted px-3 mb-0">{t('dirs.emptyList')}</p>;
    }

    return (
        <div className="crm-table-wrap px-3">
            <Table size="sm" className="VendorsDirTable mb-0">
                <thead>
                    <tr className="bg-light">
                        <td className="head-id">{t('dirs.colId')}</td>
                        <td className="head-name">{t('dirs.colName')}</td>
                        <td className="head-products text-end">{t('dirs.colUsage')}</td>
                        <td className="head-action"></td>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => {
                        const count = usage[item.ID] || 0;
                        const canDelete = count === 0;
                        const isEditing = editId === item.ID;

                        return (
                            <tr key={item.ID}>
                                <td className="id">
                                    <code>{item.ID}</code>
                                </td>
                                <td className="name">
                                    {isEditing ? (
                                        <FormInput
                                            compact
                                            showForm
                                            required
                                            requiredMessage={t('dirs.messEmpty')}
                                            setShowForm={(show) => {
                                                if (!show) setEditId(null);
                                            }}
                                            defaultValue={item.name}
                                            callBack={(value) => handleRename(item, value)}
                                        />
                                    ) : (
                                        <p
                                            className="fw-medium mb-0 text-primary product-vendor-editable"
                                            role="button"
                                            tabIndex={0}
                                            title={t('dirs.renameHint')}
                                            onClick={() => setEditId(item.ID)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setEditId(item.ID);
                                                }
                                            }}
                                        >
                                            {item.name}
                                        </p>
                                    )}
                                </td>
                                <td className="products text-end text-muted">{count}</td>
                                <td className="action text-end">
                                    <EToolTip title={canDelete ? t('dirs.deleteHint') : t('dirs.deleteDisabled')}>
                                        <span className="d-inline-block">
                                            <Button
                                                type="button"
                                                variant="link"
                                                className={`p-0 ${canDelete ? 'text-danger' : 'text-muted'}`}
                                                disabled={!canDelete}
                                                onClick={() => handleDelete(item)}
                                            >
                                                <SolarIcon name="trash" />
                                            </Button>
                                        </span>
                                    </EToolTip>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
};

export default DirsList;
